import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { XIcon } from '@phosphor-icons/react';
import { dedupedGet } from '../../lib/requestDedupe';
import DashboardBannerModal from '../../Dashboard/DashboardBannerModal';
import { openBannerLink } from '../../Dashboard/bannerLinkUtils';

const formatSessionDateTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const fetchUpcomingSessions = async () => {
  try {
    const examsRes = await dedupedGet('/api/user/profile/exams');
    const exams = examsRes?.data?.data?.exams || [];
    if (!exams.length) return [];

    const now = new Date();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const currentYear = now.getFullYear();
    const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextMonth = String(nextMonthDate.getMonth() + 1).padStart(2, '0');
    const nextYear = nextMonthDate.getFullYear();

    const scheduleRes = await dedupedGet(
      `/api/calendar/schedule?timelineStart=${currentMonth}/${currentYear}&timelineEnd=${nextMonth}/${nextYear}&examId=${exams[0]._id}`,
    );
    const liveSessions = scheduleRes?.data?.data?.liveSessions || [];

    return liveSessions
      .filter((s) => new Date(s.startDate) > now)
      .map((s) => ({
        message: `${s.title} is scheduled for ${formatSessionDateTime(s.startDate)}`,
        isTimerEnabled: false,
        isButtonEnabled: false,
        showCloseButton: false,
      }));
  } catch {
    return [];
  }
};

const Banner = ({ onClose }) => {
  const navigate = useNavigate();
  const [hellobars, setHellobars] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState({});
  const [showHelloBarModal, setShowHelloBarModal] = useState(false);

  useEffect(() => {
    const isClosed = sessionStorage.getItem('hellobarClosed');
    if (isClosed) return;

    const fetchHellobars = async () => {
      try {
        const hellobarRes = await axios.get('/api/hellobar/v2');
        const apiHellobars = hellobarRes?.data?.hellobar || [];
        setHellobars(apiHellobars);
      } catch (error) {
        console.error('Failed to fetch hellobar data:', error);
        setHellobars([]);
      }
    };

    fetchHellobars();
  }, []);

  useEffect(() => {
    if (hellobars?.length > 1 && !showHelloBarModal) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % hellobars?.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [hellobars, showHelloBarModal]);

  useEffect(() => {
    if (hellobars?.length > 0) {
      const currentHellobar = hellobars[currentIndex];
      if (currentHellobar && currentHellobar.isTimerEnabled) {
        const timer = setInterval(() => {
          const now = new Date().getTime();
          const targetDate = new Date(currentHellobar.countdownDate).getTime();
          const difference = targetDate - now;

          if (difference > 0) {
            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((difference % (1000 * 60)) / 1000);

            setTimeLeft({ days, hours, minutes, seconds });
          } else {
            clearInterval(timer);
            setTimeLeft(null);
          }
        }, 1000);

        return () => clearInterval(timer);
      } else {
        setTimeLeft(null);
      }
    }
  }, [hellobars, currentIndex]);

  const handleClose = () => {
    sessionStorage.setItem('hellobarClosed', 'true');
    setHellobars([]);
    if (onClose) {
      onClose();
    }
  };

  if (!hellobars?.length) return null;

  const currentHellobar = hellobars[currentIndex];

  const handleButtonClick = () => {
    if (currentHellobar?.modalDetails?.visible) {
      setShowHelloBarModal(true);
      return;
    }
    openBannerLink(currentHellobar?.url, navigate);
  };

  const renderCountdown = () => {
    if (timeLeft === null) {
      return <span>{currentHellobar.messageAfterCountDown}</span>;
    }
    return (
      <>
        <span className="bg-black/10 px-[6px] py-1 rounded-sm">
          {timeLeft.days}D : {timeLeft.hours}H : {timeLeft.minutes}M : {timeLeft.seconds}S
        </span>
      </>
    );
  };

  return (
    <div className="text-[var(--q3-neutral-default_inverted)] bg-[var(--q3-rose-normal)] rounded-xl flex flex-col md:flex-row gap-3 md:gap-4 md:items-center p-4 mb-6 mx-4 lg:mx-0">
      {/* Text row — close button sits here on mobile (top-right), hidden on desktop */}
      <div className="flex items-start justify-between gap-2 flex-1">
        <div className="flex flex-col gap-3 flex-1">
          {currentHellobar?.isTimerEnabled && <p>{renderCountdown()}</p>}
          <p className="text-sm md:text-base">{currentHellobar.message}</p>
        </div>
        {currentHellobar?.showCloseButton && (
          <XIcon className="cursor-pointer flex-shrink-0 md:hidden" weight="bold" onClick={handleClose} />
        )}
      </div>
      {/* CTA button — full-width on mobile, auto on desktop */}
      {currentHellobar?.isButtonEnabled && (
        <Button
          className="w-full md:w-auto flex-shrink-0 bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] hover:scale-100 hover:bg-[var(--q3-surface-dimmer)] hover:text-[var(--q3-neutral-default)] text-xs md:text-sm"
          onClick={handleButtonClick}
        >
          {currentHellobar?.buttonText}
        </Button>
      )}
      {/* Close button — desktop only (mobile version is above) */}
      {currentHellobar?.showCloseButton && (
        <XIcon className="cursor-pointer flex-shrink-0 hidden md:block" weight="bold" onClick={handleClose} />
      )}
      <DashboardBannerModal
        open={showHelloBarModal}
        onOpenChange={setShowHelloBarModal}
        modalDetails={currentHellobar?.modalDetails}
      />
    </div>
  );
};

export default Banner;
